import React, {useCallback, useMemo} from 'react';
import {DefaultEdgeOptions, Edge, Node, useReactFlow, XYPosition} from 'reactflow';
import {NodeType} from 'crqlar-react-common/dist/models/automation/automation';

import type {EdgeActions} from '../components/InsertableEdge';
import type {AddNodePickersProps} from '../components/addNode/AddNodePickers';
import {LAYOUT_LANE_SPACING, LAYOUT_ROW_SPACING} from '../utils/autoLayout';
import {
    findOpenLanePosition,
    findOpenPosition,
    getPaneCentre,
    NODE_CENTRE_OFFSET,
    NODE_FOOTPRINT
} from '../utils/nodePlacement';
import type {useNodeOperations} from './useNodeOperations';

/** The add-node picker that is open — one at a time — and what it adds to. */
export type AddNodePicker =
    | {kind: 'toolbar'; anchorEl: HTMLElement}
    | {kind: 'pane'; anchorPosition: {top: number; left: number}; flowPoint: XYPosition}
    | {kind: 'edge'; edgeId: string; anchorEl: HTMLElement; flowPoint: XYPosition}
    | {kind: 'append'; nodeId: string; sourceHandle: string | null; anchorEl: HTMLElement};

/** Branch lanes Auto layout gives these handles — an appended node lands where a re-layout would put it. */
const APPEND_LANE_DELTA: Record<string, number> = {true: -1, false: 1};

/** Clearance under a taller-than-default node, so the row spacing never swallows the gap. */
const APPEND_MIN_GAP = 60;

interface UseAddNodeGesturesOptions {
    /** Held by the editor: an open picker pauses the canvas shortcuts. */
    picker: AddNodePicker | null;
    setPicker: (picker: AddNodePicker | null) => void;
    readOnly: boolean;
    nodes: Node[];
    edges: Edge[];
    setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
    setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
    edgeDefaults: DefaultEdgeOptions;
    addNode: ReturnType<typeof useNodeOperations>['addNode'];
    /** The undo capture, taken before each change. */
    capture: () => void;
    markEdited: () => void;
}

// Toolbar Add (click or drag a row out), pane double-click, an edge's ＋ (splice) and an open handle's ＋ (append).
export const useAddNodeGestures = ({
    picker,
    setPicker,
    readOnly,
    nodes,
    edges,
    setNodes,
    setEdges,
    edgeDefaults,
    addNode,
    capture,
    markEdited
}: UseAddNodeGesturesOptions) => {
    const {screenToFlowPosition} = useReactFlow();

    const closePicker = useCallback(() => setPicker(null), [setPicker]);

    const handleAddNode = (nodeType: NodeType, label: string) => {
        const centre = screenToFlowPosition(getPaneCentre());
        const preferred = {x: centre.x - NODE_CENTRE_OFFSET.x, y: centre.y - NODE_CENTRE_OFFSET.y};

        capture();
        addNode(nodeType, label, findOpenPosition(preferred, nodes), setNodes);

        setPicker(null);
        markEdited();
    };

    const handleDropNode = (nodeType: NodeType, label: string, client: {x: number; y: number}) => {
        const position = screenToFlowPosition(client);

        capture();
        addNode(
            nodeType,
            label,
            {x: position.x - NODE_CENTRE_OFFSET.x, y: position.y - NODE_CENTRE_OFFSET.y},
            setNodes
        );

        setPicker(null);
        markEdited();
    };

    const handleDeleteEdge = useCallback((edgeId: string) => {
        if (readOnly) return;

        capture();
        setEdges(current => current.filter(edge => edge.id !== edgeId));
        markEdited();
    }, [capture, readOnly, setEdges, markEdited]);

    const handleEdgeInsertClick = useCallback((edgeId: string, anchorEl: HTMLElement, flowPoint: XYPosition) => {
        setPicker({kind: 'edge', edgeId, anchorEl, flowPoint});
    }, [setPicker]);

    const handleAppendClick = useCallback((nodeId: string, sourceHandle: string | null, anchorEl: HTMLElement) => {
        setPicker({kind: 'append', nodeId, sourceHandle, anchorEl});
    }, [setPicker]);

    const handlePaneDoubleClick = (event: React.MouseEvent) => {
        if (readOnly) return;

        const target = event.target as HTMLElement;
        if (!target.classList?.contains('react-flow__pane')) return;

        setPicker({
            kind: 'pane',
            anchorPosition: {top: event.clientY, left: event.clientX},
            flowPoint: screenToFlowPosition({x: event.clientX, y: event.clientY}),
        });
    };

    const handlePaneAddNode = (nodeType: NodeType, label: string) => {
        if (picker?.kind !== 'pane') return;

        const {flowPoint} = picker;
        setPicker(null);

        const preferred = {x: flowPoint.x - NODE_CENTRE_OFFSET.x, y: flowPoint.y - NODE_CENTRE_OFFSET.y};

        capture();
        addNode(nodeType, label, findOpenPosition(preferred, nodes), setNodes);
        markEdited();
    };

    const handleAppendNode = (nodeType: NodeType, label: string) => {
        if (picker?.kind !== 'append') return;

        const {nodeId, sourceHandle} = picker;
        const source = nodes.find(node => node.id === nodeId);
        setPicker(null);
        if (!source) return;

        const laneDelta = sourceHandle ? APPEND_LANE_DELTA[sourceHandle] ?? 0 : 0;
        const sourceHeight = source.height ?? NODE_FOOTPRINT.height;
        const preferred = {
            x: source.position.x + laneDelta * LAYOUT_LANE_SPACING,
            y: source.position.y + Math.max(LAYOUT_ROW_SPACING, sourceHeight + APPEND_MIN_GAP),
        };

        capture();
        addNode(
            nodeType,
            label,
            findOpenLanePosition(preferred, nodes),
            setNodes,
            (newNodeId: string) => {
                setEdges(current => [...current, {
                    ...edgeDefaults,
                    id: `${nodeId}-${newNodeId}-${Date.now()}`,
                    source: nodeId,
                    target: newNodeId,
                    sourceHandle: sourceHandle ?? undefined,
                }]);
            }
        );

        markEdited();
    };

    const handleInsertNode = (nodeType: NodeType, label: string) => {
        if (picker?.kind !== 'edge') return;

        const {edgeId, flowPoint} = picker;
        const original = edges.find(edge => edge.id === edgeId);
        setPicker(null);
        if (!original) return;

        capture();
        addNode(
            nodeType,
            label,
            {x: flowPoint.x - NODE_CENTRE_OFFSET.x, y: flowPoint.y - NODE_CENTRE_OFFSET.y},
            setNodes,
            (newNodeId: string) => {
                const stamp = Date.now();

                setEdges(current => [
                    ...current.filter(edge => edge.id !== edgeId),
                    {
                        ...edgeDefaults,
                        id: `${original.source}-${newNodeId}-${stamp}`,
                        source: original.source,
                        target: newNodeId,
                        sourceHandle: original.sourceHandle,
                    },
                    {
                        ...edgeDefaults,
                        id: `${newNodeId}-${original.target}-${stamp}`,
                        source: newNodeId,
                        target: original.target,
                        // The engine follows an If edge only on "true"/"false"; the old target continues on Yes.
                        sourceHandle: nodeType === NodeType.IF_CONDITION ? 'true' : undefined,
                        targetHandle: original.targetHandle,
                    },
                ]);
            }
        );

        markEdited();
    };

    const activeInsertEdgeId = picker?.kind === 'edge' ? picker.edgeId : null;

    const edgeActions = useMemo<EdgeActions>(() => ({
        readOnly,
        onInsertClick: handleEdgeInsertClick,
        onDeleteEdge: handleDeleteEdge,
        activeInsertEdgeId,
    }), [readOnly, handleEdgeInsertClick, handleDeleteEdge, activeInsertEdgeId]);

    const appendNodeId = picker?.kind === 'append' ? picker.nodeId : null;
    const appendHandle = picker?.kind === 'append' ? picker.sourceHandle : null;

    const activeAppend = useMemo(
        () => (appendNodeId ? {nodeId: appendNodeId, sourceHandle: appendHandle} : null),
        [appendNodeId, appendHandle]
    );

    const pickers: AddNodePickersProps = {
        picker,
        onClose: closePicker,
        onToolbarAdd: handleAddNode,
        onToolbarDrop: handleDropNode,
        onEdgeInsert: handleInsertNode,
        onPaneAdd: handlePaneAddNode,
        onAppend: handleAppendNode,
    };

    return {
        edgeActions,
        activeAppend,
        onAppendClick: handleAppendClick,
        openToolbarPicker: (anchorEl: HTMLElement) => setPicker({kind: 'toolbar', anchorEl}),
        onPaneDoubleClick: handlePaneDoubleClick,
        pickers
    };
};
